/**
 * Account chip + menu.
 *
 * Pure presentational component. Takes a minimal user shape, derives
 * the initials, and exposes a `Sign Out` button. Lives under
 * `components/layout` because the app sidebar (and later the
 * settings/billing pages) reuse it.
 *
 * `placement` controls where it sits:
 *   - `header` (default): a pill that drops its menu downward.
 *   - `sidebar`: a full-width chip pinned to the sidebar bottom with an
 *     up-caret, whose menu opens UPWARD. This is the live placement after
 *     the profile moved out of the top header (Wave 5).
 *
 * The user prop is typed locally (`UserChipUser`) instead of importing
 * `AuthUser` from `features/auth/types`; cross-feature imports are
 * forbidden in `components/`. The composition root passes whatever
 * `useAuth` returns and the structural shape lines up.
 */
import { CaretUp } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button/button';
import { Icon } from '@/components/ui/icon/icon';
import { paths } from '@/config/paths';

import styles from './user-chip.module.css';

export interface UserChipUser {
  email: string;
  displayName: string | null;
}

export interface UserChipProps {
  user: UserChipUser;
  onSignOut: () => void;
  /** Where the chip lives. `sidebar` opens its menu upward. */
  placement?: 'header' | 'sidebar';
  /** Icon-rail mode · show only the avatar (no name/plan/caret). The
   *  menu still opens on click with the full account details inside. */
  compact?: boolean;
  /**
   * Plan line under the name (e.g. "Creator Plan", "Creator (Trial) · 480
   * cr"). Derived from the live subscription by the composition root; this
   * component stays presentational. Falls back to "Free Plan" when absent.
   */
  planLabel?: string;
}

const iconProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  'aria-hidden': true,
  focusable: false,
} as const;

const MENU_LINKS = [
  {
    key: 'settings',
    label: 'Settings',
    to: paths.settings,
    icon: (
      <svg {...iconProps}>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5" />
      </svg>
    ),
  },
  {
    key: 'billing',
    label: 'Billing',
    to: paths.billing,
    icon: (
      <svg {...iconProps}>
        <rect x="2" y="6" width="20" height="14" rx="2" />
        <line x1="2" y1="11" x2="22" y2="11" />
      </svg>
    ),
  },
  {
    key: 'help',
    label: 'Help & Support',
    to: paths.help,
    icon: (
      <svg {...iconProps}>
        <circle cx="12" cy="12" r="10" />
        <path d="M9.1 9.1a3 3 0 1 1 4.2 4.2l-.7.7c-.7.7-1.1 1.4-1.1 2.3" />
        <circle cx="12" cy="18" r="0.5" fill="currentColor" />
      </svg>
    ),
  },
] as const;

function initialsFromUser(user: UserChipUser): string {
  const source = (user.displayName ?? user.email).trim();
  if (!source) return '?';
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? (parts[1]?.[0] ?? '') : '';
  const initials = `${first}${second}`.toUpperCase();
  return initials || source[0]!.toUpperCase();
}

export function UserChip({
  user,
  onSignOut,
  placement = 'header',
  compact = false,
  planLabel,
}: UserChipProps) {
  const [open, setOpen] = useState(false);
  const initials = initialsFromUser(user);
  const display = user.displayName ?? user.email;
  const isSidebar = placement === 'sidebar';

  // Close on outside-click and Esc · Lior, 2026-06-01: opening the
  // menu and then clicking somewhere else on the page should
  // dismiss it like any normal popover. Listener only bound while
  // the menu is open so it doesn't compete with other handlers.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (ev: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (ev.target instanceof Node && root.contains(ev.target)) return;
      setOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    // `mousedown` (not `click`) so the menu closes the moment the
    // user starts a click outside · matches the native dropdown
    // dismissal behaviour browsers use for `<select>`.
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const wrapClass = [styles.wrap, isSidebar ? styles.wrapSidebar : ''].filter(Boolean).join(' ');
  const chipClass = [
    styles.chip,
    isSidebar ? styles.chipSidebar : '',
    compact ? styles.chipCompact : '',
  ]
    .filter(Boolean)
    .join(' ');
  const menuClass = [styles.menu, isSidebar ? styles.menuSidebar : ''].filter(Boolean).join(' ');

  return (
    <div className={wrapClass} ref={rootRef}>
      <button
        type="button"
        className={chipClass}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={compact ? `Account menu for ${display}` : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.pic} aria-hidden="true">
          {initials}
        </span>
        {compact ? null : (
          <>
            <span className={styles.meta}>
              <span className={styles.name}>{display}</span>
              <span className={styles.plan}>{planLabel ?? 'Free Plan'}</span>
            </span>
            {isSidebar ? <Icon icon={CaretUp} size={16} className={styles.chev} /> : null}
          </>
        )}
      </button>

      {open ? (
        <div role="menu" className={menuClass}>
          <div className={styles.menuHead}>
            <div className={styles.menuName}>{display}</div>
            <div className={styles.menuEmail}>{user.email}</div>
          </div>
          <nav className={styles.menuLinks} aria-label="Account">
            {MENU_LINKS.map((link) => (
              <Link
                key={link.key}
                to={link.to}
                role="menuitem"
                className={styles.menuLink}
                onClick={() => setOpen(false)}
              >
                <span className={styles.menuLinkIcon}>{link.icon}</span>
                {link.label}
              </Link>
            ))}
          </nav>
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            Sign Out
          </Button>
        </div>
      ) : null}
    </div>
  );
}
